// Every face of one document, connected (PRD-0031 slices 03 and 06). Feeds both
// the overlay's face layer and its "Faces on this photo" section, so the two
// always agree — one query, one cache entry.
//
// Every fetch and every write lives here; the section, its rows and the face
// layer under `components/` stay pure (ADR-0039).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import type { FaceRowActions } from '@/components/document-overlay/FacesOnPhoto'
import type { MergeIdentitiesPair } from '@/components/faces/MergeIdentitiesDialog'
import { canDecideFaces } from '@/features/face-matching/decision-role'
import {
  decisionErrorKey,
  type FaceActionErrorKey,
  mergeErrorKey,
} from '@/features/face-matching/errors'
import {
  type IdentityMergeTarget,
  inFlight,
  invalidateFaceMatching,
  useFaceReadId,
  useIdentityMergeRequest,
} from '@/features/face-matching/hooks/useFaceMatching'
import { confirmedMerge, faceMergePair } from '@/features/face-matching/merge-pair'
import {
  type ManualIdentityNames,
  unassignedFacesKeys,
} from '@/features/identities/hooks/useUnassignedFaces'
import { ApiError } from '@/services/api/client'
import { type FaceDecision, faceMatchingApi } from '@/services/api/face-matching'
import { identitiesApi } from '@/services/api/identities'
import {
  type DocumentFacePerson,
  type DocumentFacesResponse,
  photosApi,
} from '@/services/api/photos'
import { type FacesSection, facesSection } from '../faces-section'

export const documentFacesKeys = {
  all: (caseId: string) => ['case', caseId, 'document-faces'] as const,
  document: (caseId: string, documentId: string) =>
    [...documentFacesKeys.all(caseId), documentId] as const,
}

export function useDocumentFaces(caseId: string, documentId: string | null) {
  // While AFD is off (ADR-0051) the document has no faces: no read, no face
  // layer, no Faces section, and a deep link's face focus goes inert.
  const readDocumentId = useFaceReadId(documentId)
  return useQuery<DocumentFacesResponse>({
    queryKey: documentFacesKeys.document(caseId, readDocumentId ?? ''),
    queryFn: () => photosApi.documentFaces(caseId, readDocumentId as string),
    enabled: Boolean(caseId && readDocumentId),
    // A document's faces change only on a decision, which invalidates this key
    // through `useDocumentFaceDecision` and the face-matching helpers.
    staleTime: 30_000,
  })
}

/** The merge a confirmed face's candidate proposes (ADR-0042): the dialog asks first. */
export interface FaceMergeFlow {
  /**
   * The two identities the dialog asks about, the way the merge is proposed —
   * the face's identity folds into the candidate; null while it is closed.
   */
  pair: MergeIdentitiesPair | null
  /** "Merge" on a confirmed face's candidate *personId*: open the dialog. */
  open: (faceId: string, personId: string) => void
  close: () => void
  /** Run the merge the way the analyst confirmed it: as proposed, or swapped (ADR-0052). */
  confirm: (confirmed: MergeIdentitiesPair) => void
}

export interface UseDocumentFaceSectionResult {
  /** The rows to list, and the low-quality ones folded behind their own row. */
  section: FacesSection
  isLoading: boolean
  /** The face the analyst picked in the list or on the photo, null for none. */
  selectedFaceId: string | null
  select: (faceId: string | null) => void
  /** The face a decision or a merge is in flight for — its buttons wait. */
  pendingFaceId: string | null
  /**
   * The i18n suffix of the last refusal (see `FaceActionErrorKey`); null when
   * the last action went through.
   */
  errorKey: FaceActionErrorKey | null
  /**
   * What the analyst can do on a face's row: confirm, reject, ask about the merge
   * a confirmed face's candidate proposes, open the create dialog on an
   * unassigned face. Absent for a viewer (PRD-0032 SC-036): the read names the
   * caller's role, and the section then has no decision to offer.
   */
  actions?: FaceRowActions
  /** The merge a confirmed face's candidate proposes, as its dialog asks about it (ADR-0042). */
  merge: Omit<FaceMergeFlow, 'open'>
  /** The unassigned face the create dialog is open for, null when it is closed. */
  createForFaceId: string | null
  closeCreate: () => void
  /**
   * The create's refusal: the server's own sentence for a 409 or a 422, an empty
   * string when it failed without one, null when nothing failed.
   */
  createError: string | null
  isCreating: boolean
  /** Create an identity out of the face the dialog is open for (slice 07). */
  createIdentity: (names: ManualIdentityNames) => Promise<boolean>
}

/**
 * The faces section of one document: the ordered rows, the shared selection, the
 * two decisions and the identity merge a confirmed face's candidate proposes
 * (ADR-0042).
 *
 * A decision moves this document's faces, the identity it was taken for (its
 * star and its faces card), the identity list and the *Unassigned faces* tab, so
 * all four are invalidated once the server has answered. Nothing is written
 * optimistically here: ADR-0039 lets a second confirm of the same face lose with
 * a 409, and the honest thing to show then is the refreshed read plus the
 * message, not a row that moved and came back. A merge settles the same way.
 *
 * The read names the caller's role: a viewer is handed no action at all.
 */
export function useDocumentFaceSection(
  caseId: string,
  documentId: string | null,
  focusFaceId: string | null = null,
  /** The document's file type: only a PDF's faces name a page. */
  documentType: string | null = null
): UseDocumentFaceSectionResult {
  const queryClient = useQueryClient()
  const query = useDocumentFaces(caseId, documentId)
  // A face the overlay was opened on means nothing while AFD is off.
  const focusedFaceId = useFaceReadId(focusFaceId)
  const [createForFaceId, setCreateForFaceId] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  // The overlay may be opened on a face (the identity Photos tab does, SC-005);
  // that face starts out selected so the list and the photo agree at once.
  const [picked, setPicked] = useState<string | null>(null)
  const [errorKey, setErrorKey] = useState<FaceActionErrorKey | null>(null)
  const {
    mergingFaceId,
    open: openMerge,
    ...merge
  } = useFaceMerge(caseId, query.data?.faces, setErrorKey)

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: documentFacesKeys.all(caseId) })
    void queryClient.invalidateQueries({ queryKey: unassignedFacesKeys.all(caseId) })
    void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'identities'] })
    invalidateFaceMatching(queryClient, caseId)
  }, [caseId, queryClient])

  const decision = useMutation({
    mutationFn: ({
      personId,
      faceId,
      decision: verdict,
    }: {
      personId: string
      faceId: string
      decision: FaceDecision
    }) => faceMatchingApi.decide(caseId, personId, faceId, verdict),
    onMutate: () => setErrorKey(null),
    onError: error => setErrorKey(decisionErrorKey(error)),
    onSettled: () => invalidateAll(),
  })

  // The create walks the manual-identity endpoint, which confirms the face
  // through the same decision service — so ADR-0039 (the 409, the sibling
  // cleanup, the default main photo and the recompute) holds here too.
  const create = useMutation({
    mutationFn: (names: ManualIdentityNames) =>
      identitiesApi.createFromFaces(caseId, {
        ...names,
        face_ids: createForFaceId ? [createForFaceId] : [],
      }),
    onSuccess: invalidateAll,
  })

  const section = useMemo(
    () => facesSection(query.data?.faces, documentType),
    [query.data, documentType]
  )
  const select = useCallback((faceId: string | null) => {
    setErrorKey(null)
    setPicked(faceId)
  }, [])

  const actions: FaceRowActions = {
    onConfirm: (faceId, personId) => decision.mutate({ faceId, personId, decision: 'confirm' }),
    onReject: (faceId, personId) => decision.mutate({ faceId, personId, decision: 'reject' }),
    onMerge: openMerge,
    onCreateIdentity: faceId => {
      setCreateError(null)
      setCreateForFaceId(faceId)
    },
  }

  return {
    section,
    isLoading: query.isLoading,
    selectedFaceId: picked ?? focusedFaceId,
    select,
    pendingFaceId: inFlight(decision) ?? mergingFaceId,
    errorKey,
    actions: canDecideFaces(query.data?.member_role) ? actions : undefined,
    merge,
    createForFaceId,
    closeCreate: () => setCreateForFaceId(null),
    createError,
    isCreating: create.isPending,
    createIdentity: async names => {
      setCreateError(null)
      try {
        await create.mutateAsync(names)
        setCreateForFaceId(null)
        return true
      } catch (caught) {
        setCreateError(caught instanceof ApiError ? caught.message : '')
        return false
      }
    },
  }
}

/**
 * The overlay's merge (ADR-0042): the pair the dialog asks about, and running it.
 * On a confirmed face each candidate is another identity the face's person may
 * be, so the merge folds the face's identity into that candidate's — or the
 * candidate's into the face's, once the analyst swapped them (ADR-0052). Like
 * the decisions, nothing moves before the server answers — every read a merge
 * touches is re-read once it settles, and a page showing the merged identity
 * follows the survivor (`useMergedInto`).
 */
function useFaceMerge(
  caseId: string,
  faces: readonly DocumentFacePerson[] | undefined,
  onRefused: (key: FaceActionErrorKey | null) => void
): FaceMergeFlow & { mergingFaceId: string | null } {
  const request = useIdentityMergeRequest(caseId)
  const [target, setTarget] = useState<IdentityMergeTarget | null>(null)
  const pair = useMemo(() => faceMergePair(faces, target), [faces, target])

  return {
    pair,
    open: (faceId, personId) => setTarget({ faceId, personId }),
    close: () => setTarget(null),
    confirm: confirmed => {
      if (!target) return
      onRefused(null)
      request.mutate(
        { ...target, ...confirmedMerge(confirmed) },
        { onError: error => onRefused(mergeErrorKey(error)) }
      )
    },
    mergingFaceId: inFlight(request),
  }
}
